
import { Component, Prop } from 'vue-property-decorator';
import EventBus from '@/utils/event-bus';
import Navigation from '@/model/navigation';
import ObjectWithSignature from '@/utils/interfaces/object-with-signature';
import AppVue from '@/utils/app-vue';
import { AppLanguageHandler, AppLanguages } from '@/utils/handlers/app-language-handler';
import { Meta } from '@/utils/decorators';

import Expertise from '@/components/expertise/expertise';
import Demo from '@/components/demo/demo';
import Moodreels from '@/components/moodreels/moodreels';
import Customers from '@/components/customers/customers';
import About from '@/components/about/about';
import Contact from '@/components/contact/contact';
import BurgerNavbar from '@/components/burger-navbar/burger-navbar';

@Component({
    components: {
        Expertise,
        Demo,
        Moodreels,
        Customers,
        About,
        Contact,
        BurgerNavbar,
    },
})
export default class Cineseries extends AppVue {

    @Meta
    private metaInfo() {
        return this.languageMetaInfo;
    }

    @Prop(String) private readonly lang!: string;

    private showPage: boolean = false;

    private showBurgerNavbar: boolean = false;

    private currentNavTitle: string = '';

    private get navigationItems(): Navigation[] {

        return [
            new Navigation(this, 'expertise', 'startups', 0),
            new Navigation(this, 'demo', 'startups', 1),
            new Navigation(this, 'moodreels', 'startups', 2),
            new Navigation(this, 'customers', 'startups', 3),
            new Navigation(this, 'about', 'startups', 4),
            new Navigation(this, 'contact', 'startups', 5),
        ];

    }

    private get options(): ObjectWithSignature {

        return {
            licenseKey: process.env.VUE_APP_FULLPAGEJS_LICENSE_KEY,
            onLeave: this.onLeave,
            afterLoad: this.afterLoad,
            verticalCentered: false,
            scrollOverflow: false,
            scrollOverflowReset: true,
            autoScrolling: true,
            responsiveWidth: 740,
            lockAnchors: true,
        };

    }

    private get fullPageApi(): ObjectWithSignature {

        return (this.$refs.fullpage as ObjectWithSignature).api;

    }

    private get languageMetaInfo(): ObjectWithSignature {

        return {

            meta: [
                { name: 'description', content: this.$t('strings.meta.description') },
                { name: 'keyboards', content: this.$t('strings.meta.keyboards') },
            ],

        };

    }

    private created(): void {

        this.$store.commit('dataModule/setBackgroundOnLoading', true);

        // Set background video url
        const mobileWidth: number = 740;
        this.$store.commit('dataModule/setBackgroundUrl', window.innerWidth > mobileWidth ? 'page_bonheur_autres' : 'page_without_us');

    }

    private mounted(): void {

        AppLanguageHandler.determineLang(this, this.lang);

        EventBus.$on('background-video-loaded', () => {

            this.showPage = true;
            this.$store.commit('dataModule/setBackgroundOnLoading', false);

            setTimeout(() => {

                this.handleNavbarApparition();
                this.allSectionsMayAppear();

            }, 500);

        });

        EventBus.$on('go-to-item', (item: Navigation) => this.onNavigationItemClick(item));

        window.addEventListener('resize', this.allSectionsMayAppear);

    }

    private updated(): void {

        this.handleCssStyle();

    }

    private destroyed(): void {

        EventBus.$off([
            'background-video-loaded',
            'go-to-item',
        ]);

        window.removeEventListener('resize', this.allSectionsMayAppear);

    }

    /*
     * Stop the opacity animation for small screens.
     */
    private allSectionsMayAppear(): void {

        const sections = this.$el.querySelectorAll('.page-section') as NodeListOf<HTMLElement>;

        sections.forEach((section: HTMLElement) => {

            window.innerWidth < 920 ?
                section.classList.add('is-responsive') : section.classList.remove('.is-responsive');

        });

    }

    /*
     * Add a small animation to navbar apparition.
     */
    private handleNavbarApparition(): void {

        const nav: HTMLElement = this.$refs.nav as HTMLElement;

        if (nav) { nav.style.top = '20px'; }

    }

    /*
     * Go to next section when click on home scrolling infos.
     */
    private onScrollInfosClick(): void {

        this.fullPageApi.moveSectionDown();

    }

    /*
     * Go to section when click on menu.
     */
    private onNavigationItemClick(item: Navigation): void {

        this.fullPageApi.moveTo(item.target);

    }

    private onLeave(origin: any, destination: any, direction: any): void {

        // background and arrow down

        const arrowDown: HTMLElement = this.$el.querySelector('#arrowDown') as HTMLElement;

        let newBackground: string;

        const mobileWidth: number = 740;
        const interWidth: number = 1700;

        switch (destination.index) {

            case 0: // expertise
                newBackground = window.innerWidth > mobileWidth ? 'page_bonheur_autres' : 'page_without_us';
                arrowDown.style.top = '-100px';
                arrowDown.style.left = this.lang === AppLanguages.FR ?
                    window.innerWidth > interWidth ? '325px' : '240px' :
                    window.innerWidth > interWidth ? '300px' : '225px';
                break;
            case 1: // demo
                newBackground = window.innerWidth > mobileWidth ? 'page_noise' : 'page_without_us';
                arrowDown.style.top = '-100px';
                arrowDown.style.left = this.lang === AppLanguages.FR ?
                    window.innerWidth > interWidth ? '325px' : '240px' :
                    window.innerWidth > interWidth ? '300px' : '225px';
                break;
            case 2: // moodreels
                newBackground = window.innerWidth > mobileWidth ? 'page_green_alert' : 'page_without_us';
                arrowDown.style.top = '35px';
                arrowDown.style.left = this.lang === AppLanguages.FR ?
                    window.innerWidth > interWidth ? '325px' : '240px' :
                    window.innerWidth > interWidth ? '300px' : '225px';
                break;
            case 3: // customers
                newBackground = window.innerWidth > mobileWidth ? 'page_noise' : 'page_without_us';
                arrowDown.style.top = '35px';
                arrowDown.style.left = this.lang === AppLanguages.FR ?
                    window.innerWidth > interWidth ? '460px' : '350px' :
                    window.innerWidth > interWidth ? '405px' : '305px';
                break;
            case 4: // about
                newBackground = window.innerWidth > mobileWidth ? 'page_silure' : 'page_without_us';
                arrowDown.style.top = '35px';
                arrowDown.style.left = this.lang === AppLanguages.FR ?
                    window.innerWidth > interWidth ? '605px' : '455px' :
                    window.innerWidth > interWidth ? '500px' : '375px';
                break;
            default: // home, contact
                newBackground = 'page_without_us';
                arrowDown.style.top = '-100px';
                arrowDown.style.left = this.lang === AppLanguages.FR ?
                    window.innerWidth > interWidth ? '605px' : '455px' :
                    window.innerWidth > interWidth ? '500px' : '375px';
                break;

        }

        this.$store.commit('dataModule/setBackgroundUrl', newBackground);

        const navEL = this.$el.querySelector('#nav-' + destination.index) as HTMLElement;

        const nav: NodeListOf<HTMLElement> =
            this.$el.querySelectorAll('nav ul li') as NodeListOf<HTMLElement>;

        nav.forEach((navElement: HTMLElement) => {

            navElement.classList.remove('active-nav');

        });

        navEL.classList.add('active-nav');

    }

    private afterLoad(origin: any, destination: any, direction: any): void {

        const pages: NodeListOf<HTMLElement> = this.$el.querySelectorAll('.page-section') as NodeListOf<HTMLElement>;

        pages.forEach((pageElement: HTMLElement) => {

            pageElement.classList.remove('active-page');

        });

        const pageEl = this.$el.querySelector('#page-' + destination.index) as HTMLElement;

        if (pageEl) {

            pageEl.classList.add('active-page');

        }

        const activeNav: HTMLElement = this.$el.querySelector('.active-nav') as HTMLElement;

        this.currentNavTitle = activeNav ? activeNav.textContent!!.toUpperCase() : '';

    }

}
